import React, { FC, useState } from 'react'
import { Tag } from '../hooks/useChat'

type Props = {
    filterUnread: boolean
    onToggleUnread: (val: boolean) => void

    filterDateFrom: string
    onSetDateFrom: (val: string) => void

    filterDateTo: string
    onSetDateTo: (val: string) => void

    filterTags: number[]
    onSetTags: (tags: number[]) => void

    availableTags: Tag[]

    // New Filters
    assignedOnly?: boolean
    onToggleAssignedOnly?: (value: boolean) => void
    filterWhatsapp: boolean
    onToggleWhatsapp: (active: boolean) => void
    filterSms: boolean
    onToggleSms: (active: boolean) => void
}

const ChatFilters: FC<Props> = ({
    filterUnread,
    onToggleUnread,
    filterDateFrom,
    onSetDateFrom,
    filterDateTo,
    onSetDateTo,
    filterTags,
    onSetTags,
    availableTags,
    assignedOnly,
    onToggleAssignedOnly,
    filterWhatsapp,
    onToggleWhatsapp,
    filterSms,
    onToggleSms
}) => {
    const [isOpen, setIsOpen] = useState(false)

    const toggleTag = (tagId: number) => {
        if (filterTags.includes(tagId)) {
            onSetTags(filterTags.filter(id => id !== tagId))
        } else {
            onSetTags([...filterTags, tagId])
        }
    }

    const clearFilters = () => {
        onToggleUnread(false)
        onSetDateFrom('')
        onSetDateTo('')
        onSetTags([])
        if (onToggleAssignedOnly) onToggleAssignedOnly(false)
        onToggleWhatsapp(false)
        onToggleSms(false)
    }

    const activeFiltersCount =
        (filterUnread ? 1 : 0) +
        (assignedOnly ? 1 : 0) +
        (filterWhatsapp ? 1 : 0) +
        (filterSms ? 1 : 0) +
        (filterDateFrom ? 1 : 0) +
        (filterDateTo ? 1 : 0) +
        filterTags.length

    return (
        <div className='position-relative d-inline-block'>
            <button
                className={`btn btn-sm ${activeFiltersCount > 0 ? 'btn-primary' : 'btn-light'} d-flex align-items-center gap-2`}
                onClick={() => setIsOpen(!isOpen)}
            >
                <i className='bi bi-funnel fs-3'></i>
                Filters
                {activeFiltersCount > 0 && (
                    <span className='badge badge-circle badge-white text-primary ms-1'>{activeFiltersCount}</span>
                )}
            </button>

            {isOpen && (
                <div
                    className='card shadow-sm position-absolute p-4'
                    style={{
                        zIndex: 100,
                        top: '100%',
                        left: 0,
                        width: '300px',
                        marginTop: '5px'
                    }}
                >
                    <div className='d-flex justify-content-between align-items-center mb-4'>
                        <h4 className='card-title m-0 fs-5 fw-bold text-gray-800'>Advanced Filters</h4>
                        <button className='btn btn-icon btn-sm btn-light-danger' onClick={() => setIsOpen(false)}>
                            <i className='bi bi-x fs-2'></i>
                        </button>
                    </div>

                    {/* Status / Ownership */}
                    <div className='mb-4'>
                        <label className='form-label fw-bold text-gray-700'>Status</label>
                        <div className='d-flex flex-column gap-2'>
                            <div className='form-check form-switch form-check-custom form-check-solid'>
                                <input
                                    className='form-check-input'
                                    type='checkbox'
                                    checked={filterUnread}
                                    onChange={(e) => onToggleUnread(e.target.checked)}
                                    id='filterUnread'
                                />
                                <label className='form-check-label text-gray-700' htmlFor='filterUnread'>
                                    Unread Only
                                </label>
                            </div>
                            <div className='form-check form-switch form-check-custom form-check-solid'>
                                <input
                                    className='form-check-input'
                                    type='checkbox'
                                    checked={assignedOnly}
                                    onChange={(e) => onToggleAssignedOnly && onToggleAssignedOnly(e.target.checked)}
                                    id='assignedOnlyCheck'
                                />
                                <label className='form-check-label text-gray-700' htmlFor='assignedOnlyCheck'>
                                    Assigned to me
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Channels */}
                    <div className='mb-4'>
                        <label className='form-label fw-bold text-gray-700'>Channels</label>
                        <div className='d-flex gap-4'>
                            <div className='form-check form-check-custom form-check-solid form-check-sm'>
                                <input
                                    className='form-check-input'
                                    type='checkbox'
                                    checked={filterWhatsapp}
                                    onChange={(e) => onToggleWhatsapp(e.target.checked)}
                                    id='whatsappCheck'
                                />
                                <label className='form-check-label text-gray-700' htmlFor='whatsappCheck'>
                                    WhatsApp
                                </label>
                            </div>
                            <div className='form-check form-check-custom form-check-solid form-check-sm'>
                                <input
                                    className='form-check-input'
                                    type='checkbox'
                                    checked={filterSms}
                                    onChange={(e) => onToggleSms(e.target.checked)}
                                    id='smsCheck'
                                />
                                <label className='form-check-label text-gray-700' htmlFor='smsCheck'>
                                    SMS
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* Date Range */}
                    <div className='mb-4'>
                        <label className='form-label fw-bold text-gray-700'>Date Range</label>
                        <div className='d-flex gap-2 flex-column'>
                            <input
                                type='date'
                                className='form-control form-control-sm form-control-solid'
                                value={filterDateFrom}
                                onChange={(e) => onSetDateFrom(e.target.value)}
                                placeholder='From'
                            />
                            <input
                                type='date'
                                className='form-control form-control-sm form-control-solid'
                                value={filterDateTo}
                                onChange={(e) => onSetDateTo(e.target.value)}
                                placeholder='To'
                            />
                        </div>
                    </div>

                    {/* Tags */}
                    <div className='mb-5'>
                        <label className='form-label fw-bold text-gray-700'>Tags</label>
                        <div className='d-flex flex-wrap gap-2'>
                            {availableTags.map(tag => (
                                <span
                                    key={tag.id}
                                    className={`badge cursor-pointer ${filterTags.includes(tag.id) ? 'badge-primary' : 'badge-light'}`}
                                    style={filterTags.includes(tag.id) ? { backgroundColor: tag.color } : { border: `1px solid ${tag.color}`, color: tag.color }}
                                    onClick={() => toggleTag(tag.id)}
                                >
                                    {tag.name}
                                </span>
                            ))}
                            {availableTags.length === 0 && (
                                <span className='text-muted fs-7'>No tags available</span>
                            )}
                        </div>
                    </div>

                    {/* Actions */}
                    <div className='d-flex justify-content-end'>
                        <button className='btn btn-sm btn-light me-2' onClick={clearFilters}>
                            Clear
                        </button>
                        <button className='btn btn-sm btn-primary' onClick={() => setIsOpen(false)}>
                            Apply
                        </button>
                    </div>
                </div>
            )}

            {/* Overlay to close when clicking outside (optional, simplified) */}
            {isOpen && (
                <div
                    style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 90 }}
                    onClick={() => setIsOpen(false)}
                />
            )}
        </div>
    )
}

export default ChatFilters
